import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { FiArrowRight, FiArrowLeft, FiCheck, FiSun, FiTool, FiCheckCircle, FiMail, FiHome, FiLayout, FiTruck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Configurator = () => {
  const [category, setCategory] = useState(null);
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({});

  const selectCategory = (cat) => {
    setCategory(cat);
    setStep(1);
    setConfig({
      produkt_typ: cat.title,
      material: 'Aluminium',
      farbe: 'RAL 7016 Anthrazit',
      extras: [],
      montage: 'Mit Montage'
    });
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const toggleExtra = (extra) => {
    setConfig(prev => ({
      ...prev,
      extras: prev.extras.includes(extra)
        ? prev.extras.filter(e => e !== extra)
        : [...prev.extras, extra]
    }));
  };

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(31, 182, 255); // MS-Blue
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text('Ihre Konfiguration', 15, 20);
    doc.setFontSize(12);
    doc.text(`Oscar Terrassen - Fachbereich ${category.title}`, 15, 30);

    // Data Table
    const tableData = Object.entries(config).map(([key, val]) => [
      key.replace('_', ' ').toUpperCase(),
      Array.isArray(val) ? val.join(', ') : val
    ]);

    doc.autoTable({
      startY: 50,
      head: [['Eigenschaft', 'Auswahl']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillStyle: [10, 79, 163] } // MS-Solar
    });

    doc.save(`Oscar-Konfiguration-${category.title}.pdf`);
  };

  if (!category) {
    return <CategorySelection onSelect={selectCategory} />;
  }

  return (
    <div className="min-h-screen bg-ms-light py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Bar Header */}
        <div className="mb-8 flex justify-between items-center bg-white p-4 rounded-2xl shadow-sm">
          <button onClick={() => setCategory(null)} className="text-ms-dark/40 hover:text-ms-blue flex items-center gap-2 font-black uppercase text-xs tracking-widest">
            <SafeIcon icon={FiArrowLeft} /> Andere Kategorie
          </button>
          <div className="px-4 py-1 bg-ms-blue/10 text-ms-blue rounded-full text-[10px] font-black uppercase tracking-widest">
            {category.title} Konfigurator
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 border border-white min-h-[600px] flex flex-col">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex-grow"
            >
              {category.id === 'terrassenueberdachung' && <TerraceSteps step={step} config={config} updateConfig={updateConfig} toggleExtra={toggleExtra} />}
              {category.id === 'carport' && <CarportSteps step={step} config={config} updateConfig={updateConfig} toggleExtra={toggleExtra} />}
              {category.id === 'wintergarten' && <WintergartenSteps step={step} config={config} updateConfig={updateConfig} toggleExtra={toggleExtra} />}
              {category.id === 'pergola' && <PergolaSteps step={step} config={config} updateConfig={updateConfig} toggleExtra={toggleExtra} />}
              
              {step === 9 && <FinalSummary config={config} />}
              {step === 10 && <FinalForm />}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="mt-12 pt-8 border-t border-ms-light flex flex-col sm:flex-row gap-6 justify-between items-center">
            {step > 1 && step < 10 && (
              <button onClick={prevStep} className="font-black uppercase tracking-widest text-ms-dark/40 hover:text-ms-dark flex items-center gap-2">
                <SafeIcon icon={FiArrowLeft} /> Zurück
              </button>
            )}
            
            <div className="sm:ml-auto flex gap-4 w-full sm:w-auto">
              {step < 9 ? (
                <button
                  onClick={nextStep}
                  className="w-full sm:w-auto bg-ms-blue text-white px-10 py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl hover:-translate-y-1 flex items-center justify-center gap-2"
                >
                  Weiter <SafeIcon icon={FiArrowRight} />
                </button>
              ) : step === 9 ? (
                <>
                  <button onClick={generatePDF} className="bg-ms-solar text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-white text-ms-solar flex items-center justify-center text-[10px]">1</div> PDF Speichern
                  </button>
                  <button onClick={nextStep} className="bg-ms-red text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-white text-ms-red flex items-center justify-center text-[10px]">2</div> Weiter
                  </button>
                </>
              ) : (
                <div className="flex gap-4">
                  <button onClick={() => setStep(9)} className="text-ms-dark/40 font-black uppercase tracking-widest text-xs">Zurück</button>
                  <NavLink to="/" className="bg-ms-blue text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest flex items-center gap-2 shadow-lg">
                    <SafeIcon icon={FiHome} /> Startseite
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- Subcomponents --- */

const CategorySelection = ({ onSelect }) => {
  const cats = [
    { id: 'terrassenueberdachung', title: 'Terrasse', icon: FiHome, color: 'bg-ms-blue' },
    { id: 'carport', title: 'Carport', icon: FiTruck, color: 'bg-ms-solar' },
    { id: 'wintergarten', title: 'Wintergarten', icon: FiLayout, color: 'bg-ms-dark' },
    { id: 'pergola', title: 'Pergola', icon: FiSun, color: 'bg-ms-solar' },
  ];

  return (
    <div className="min-h-screen bg-ms-light py-20 px-4">
      <div className="max-w-6xl mx-auto text-center">
        <h1 className="text-5xl md:text-7xl font-black text-ms-dark uppercase tracking-tighter mb-4">Konfigurator</h1>
        <p className="text-xl text-ms-dark/50 font-medium mb-16 italic">Wählen Sie Ihr gewünschtes Projekt aus:</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cats.map(cat => (
            <button
              key={cat.id}
              onClick={() => onSelect(cat)}
              className="group bg-white p-12 rounded-[3.5rem] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 border border-white flex flex-col items-center"
            >
              <div className={`${cat.color} text-white w-20 h-20 rounded-3xl flex items-center justify-center text-4xl mb-8 group-hover:scale-110 transition-transform`}>
                <SafeIcon icon={cat.icon} />
              </div>
              <h3 className="text-2xl font-black text-ms-dark uppercase tracking-tight mb-2">{cat.title}</h3>
              <p className="text-xs font-black text-ms-blue uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">Jetzt Planen</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const TerraceSteps = ({ step, config, updateConfig, toggleExtra }) => (
  <div className="space-y-8">
    {step === 1 && <OptionStep title="Bauart" options={['Wandmontage', 'Freistehend']} field="bauart" current={config.bauart} onSelect={updateConfig} />}
    {step === 2 && <DimensionsStep config={config} updateConfig={updateConfig} fields={['Breite', 'Tiefe', 'Höhe']} />}
    {step === 3 && <OptionStep title="Dachauflage" options={['Glas (VSG)', 'Polycarbonat Klar', 'Polycarbonat Opal']} field="dachtyp" current={config.dachtyp} onSelect={updateConfig} />}
    {step === 4 && <ColorStep current={config.farbe} onSelect={updateConfig} />}
    {step === 5 && <OptionStep title="Seitenelemente" options={['Keine', 'Glasschiebewände', 'Feste Keilfenster', 'ZIP-Screens']} field="seitenelemente" current={config.seitenelemente} onSelect={updateConfig} />}
    {step === 6 && <ExtrasStep options={['LED-Spots', 'Heizstrahler', 'Unterdachmarkise', 'Regenrinne']} selected={config.extras} onToggle={toggleExtra} />}
    {step >= 7 && step <= 8 && <MontageStep current={config.montage} onSelect={updateConfig} />}
  </div>
);

const CarportSteps = ({ step, config, updateConfig, toggleExtra }) => (
  <div className="space-y-8">
    {step === 1 && <OptionStep title="Carport Typ" options={['Einzelcarport', 'Doppelcarport', 'Reihencarport']} field="bauart" current={config.bauart} onSelect={updateConfig} />}
    {step === 2 && <DimensionsStep config={config} updateConfig={updateConfig} fields={['Breite', 'Tiefe', 'Höhe']} />}
    {step === 3 && <OptionStep title="Eindeckung" options={['Stahltrapezblech', 'Polycarbonat', 'PV-Solarmodule', 'Holz-Design']} field="dachtyp" current={config.dachtyp} onSelect={updateConfig} />}
    {step === 4 && <ColorStep current={config.farbe} onSelect={updateConfig} />}
    {step === 5 && <OptionStep title="Wandelemente" options={['Ohne Wände', 'Holz-Lamellen', 'Alu-Paneele', 'Geräteraum integriert']} field="seitenelemente" current={config.seitenelemente} onSelect={updateConfig} />}
    {step === 6 && <ExtrasStep options={['E-Auto Ladestation', 'Bewegungsmelder', 'Dachrinne', 'Fundament-Service']} selected={config.extras} onToggle={toggleExtra} />}
    {step >= 7 && step <= 8 && <MontageStep current={config.montage} onSelect={updateConfig} />}
  </div>
);

const WintergartenSteps = ({ step, config, updateConfig, toggleExtra }) => (
  <div className="space-y-8">
    {step === 1 && <OptionStep title="System" options={['Kaltwintergarten', 'Wohnwintergarten (Isoliert)']} field="bauart" current={config.bauart} onSelect={updateConfig} />}
    {step === 2 && <DimensionsStep config={config} updateConfig={updateConfig} fields={['Breite', 'Tiefe', 'Höhe']} />}
    {step === 3 && <OptionStep title="Verglasung" options={['2-fach Isolierglas', '3-fach Energieglas', 'Sonnenschutzglas']} field="dachtyp" current={config.dachtyp} onSelect={updateConfig} />}
    {step === 4 && <ColorStep current={config.farbe} onSelect={updateConfig} />}
    {step === 5 && <OptionStep title="Öffnungstyp" options={['Schiebetüren', 'Faltanlagen', 'Dreh-Kipp-Türen']} field="seitenelemente" current={config.seitenelemente} onSelect={updateConfig} />}
    {step === 6 && <ExtrasStep options={['Klimatisierung', 'Automatische Lüftung', 'Insektenschutz integriert', 'Smart-Home Steuerung']} selected={config.extras} onToggle={toggleExtra} />}
    {step >= 7 && step <= 8 && <MontageStep current={config.montage} onSelect={updateConfig} />}
  </div>
);

const PergolaSteps = ({ step, config, updateConfig, toggleExtra }) => (
  <div className="space-y-8">
    {step === 1 && <OptionStep title="Pergola Typ" options={['Bioklimatisch (Lamellen)', 'Faltdach (Stoff)', 'Festes Glasdach']} field="bauart" current={config.bauart} onSelect={updateConfig} />}
    {step === 2 && <DimensionsStep config={config} updateConfig={updateConfig} fields={['Breite', 'Tiefe', 'Höhe']} />}
    {step === 3 && <ColorStep current={config.farbe} onSelect={updateConfig} />}
    {step === 4 && <OptionStep title="Steuerung" options={['Handsender', 'App-Steuerung', 'Wandschalter']} field="steuerung" current={config.steuerung} onSelect={updateConfig} />}
    {step === 5 && <ExtrasStep options={['Regensensor', 'Windsensor', 'LED-Stripes integriert', 'Heizmodule']} selected={config.extras} onToggle={toggleExtra} />}
    {step >= 6 && step <= 8 && <MontageStep current={config.montage} onSelect={updateConfig} />}
  </div>
);

/* Helper Components */

const OptionStep = ({ title, options, field, current, onSelect }) => (
  <div className="space-y-6">
    <h2 className="text-3xl font-black text-ms-dark uppercase tracking-tighter leading-none">{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onSelect(field, opt)}
          className={`p-8 rounded-2xl border-2 transition-all font-black uppercase tracking-widest text-sm ${
            current === opt ? 'border-ms-blue bg-ms-blue/5' : 'border-ms-light hover:border-ms-blue/30'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const DimensionsStep = ({ config, updateConfig, fields = ['Breite', 'Tiefe', 'Höhe'] }) => (
  <div className="space-y-6">
    <h2 className="text-3xl font-black text-ms-dark uppercase tracking-tighter leading-none">Abmessungen (mm)</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {fields.map(dim => {
        const key = dim.toLowerCase().replace('ö', 'oe');
        return (
          <div key={dim} className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-ms-blue ml-2">{dim}</label>
            <input
              type="number"
              value={config[key] || ''}
              onChange={(e) => updateConfig(key, e.target.value)}
              placeholder={`z.B. ${dim === 'Höhe' ? '2200' : '4000'}`}
              className="w-full p-6 bg-ms-light rounded-2xl border-none focus:ring-4 focus:ring-ms-blue/20 font-black text-lg outline-none"
            />
          </div>
        );
      })}
    </div>
  </div>
);

const ColorStep = ({ current, onSelect }) => (
  <div className="space-y-6">
    <h2 className="text-3xl font-black text-ms-dark uppercase tracking-tighter leading-none">Farbe</h2>
    <div className="grid grid-cols-1 gap-4">
      {['RAL 7016 Anthrazit', 'RAL 9016 Weiß', 'RAL 9006 Silber', 'Sonderfarbe'].map(c => (
        <button
          key={c}
          onClick={() => onSelect('farbe', c)}
          className={`p-6 rounded-2xl border-2 flex items-center gap-4 transition-all font-black uppercase tracking-widest text-sm ${
            current === c ? 'border-ms-blue bg-ms-blue/5' : 'border-ms-light hover:border-ms-blue/30'
          }`}
        >
          <div className={`w-8 h-8 rounded-full border ${c.includes('Anthrazit') ? 'bg-[#383E42]' : c.includes('Weiß') ? 'bg-white' : 'bg-[#A5A8A6]'}`}></div>
          {c}
        </button>
      ))}
    </div>
  </div>
);

const ExtrasStep = ({ options, selected, onToggle }) => (
  <div className="space-y-6">
    <h2 className="text-3xl font-black text-ms-dark uppercase tracking-tighter leading-none">Extras</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {options.map(e => (
        <button
          key={e}
          onClick={() => onToggle(e)}
          className={`p-6 rounded-2xl border-2 flex items-center justify-between transition-all font-black uppercase tracking-widest text-sm ${
            selected.includes(e) ? 'border-ms-blue bg-ms-blue/5' : 'border-ms-light hover:border-ms-blue/30'
          }`}
        >
          {e}
          {selected.includes(e) && <SafeIcon icon={FiCheck} className="text-ms-blue" />}
        </button>
      ))}
    </div>
  </div>
);

const MontageStep = ({ current, onSelect }) => (
  <div className="space-y-6">
    <h2 className="text-3xl font-black text-ms-dark uppercase tracking-tighter leading-none">Service</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {['Mit Montage', 'Nur Material'].map(m => (
        <button
          key={m}
          onClick={() => onSelect('montage', m)}
          className={`p-10 rounded-3xl border-2 flex flex-col items-center gap-4 transition-all ${
            current === m ? 'border-ms-blue bg-ms-blue/5' : 'border-ms-light hover:border-ms-blue/30'
          }`}
        >
          <SafeIcon icon={m === 'Mit Montage' ? FiTool : FiCheckCircle} className="text-3xl text-ms-blue" />
          <span className="font-black uppercase tracking-widest text-sm">{m}</span>
        </button>
      ))}
    </div>
  </div>
);

const FinalSummary = ({ config }) => (
  <div className="space-y-8">
    <h2 className="text-3xl font-black text-ms-dark uppercase tracking-tighter leading-none">Zusammenfassung</h2>
    <div className="bg-ms-solar/5 p-8 rounded-[2.5rem] border border-ms-solar/20">
      <div className="flex items-center gap-3 mb-4">
        <SafeIcon icon={FiMail} className="text-ms-solar text-2xl" />
        <h3 className="text-lg font-black text-ms-solar uppercase tracking-tight">Anleitung</h3>
      </div>
      <p className="text-ms-dark/80 font-bold leading-relaxed">
        1. Klicken Sie auf <span className="text-ms-solar font-black">"PDF Speichern"</span>.<br />
        2. Klicken Sie auf <span className="text-ms-red font-black">"Weiter"</span> zum Anfrage-Formular.
      </p>
    </div>
    <div className="grid md:grid-cols-2 gap-6 bg-ms-light p-8 rounded-3xl">
      {Object.entries(config).map(([k, v]) => (
        <div key={k}>
          <p className="text-[10px] font-black uppercase text-ms-blue tracking-widest">{k.replace('_', ' ')}</p>
          <p className="font-black uppercase text-sm">{Array.isArray(v) ? v.join(', ') : v}</p>
        </div>
      ))}
    </div>
  </div>
);

const FinalForm = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://embed.formaloo.me/v1/main.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-black text-ms-dark uppercase tracking-tighter leading-none">Anfrage senden</h2>
      <div className="rounded-[2rem] overflow-hidden border border-ms-light shadow-inner">
        <div className="formaloo--root-container" data-form-slug="kzmNjQ5p" style={{ width: '100%', height: '750px' }}></div>
      </div>
    </div>
  );
};

export default Configurator;